import { FC, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { AnnouncementActionEvent } from '@bonusly/common-announcements';
import { Box, Flex, rem, Text, VStack } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useBuildMonarchURL, useBuildRecognizeURL } from '@bonusly/url';

import { useIsRecognize } from '../../../../hooks/use-is-recognize';
import { isIOSDevice } from '../../../../utils/browser';
import { Route } from '../../config/router';
import { Notification, NotificationStatus } from '../../lib/notification/notification';
import { pathWithId } from '../../utils/api';
import { DropdownOption } from '../Dropdown';
import { useGetIcon } from './hooks/useGetIcon';
import { useInvalidateNotifications } from './hooks/useInvalidateNotifications';
import { NotificationUpdateType, useUpdateNotifications } from './hooks/useUpdateNotifications';
import { UnreadBadge } from './UnreadBadge';

interface NotificationProps {
	notification: Notification;
	handleClose: VoidFunction;
}
export const NotificationItem: FC<NotificationProps> = ({ notification, handleClose }) => {
	const recognizeURL = useBuildRecognizeURL();
	const monarchURL = useBuildMonarchURL();
	const invalidate = useInvalidateNotifications();
	const ref = useRef<HTMLDivElement>(null);
	const icon = useGetIcon({ notification: notification });
	const navigate = useNavigate();
	const isRecognize = useIsRecognize();
	const { track } = useAnalytics();
	const dispatch = useDispatch();

	const updateNotification = useUpdateNotifications(notification.id, {
		onSuccess: async () => {
			track(AnalyticsEvent.TopNavbar.NotificationAction, {
				id: notification.id,
				type: notification.type,
			});

			invalidate();
		},
	});

	const handleClick = () => {
		if (notification.status !== NotificationStatus.Read) {
			updateNotification.mutate({
				notificationId: notification.id,
				type: NotificationUpdateType.MarkAsRead,
			});
		}

		if (!notification.action) {
			dispatch('init-give-recognition');
			handleClose();
			return;
		}

		const bonusId = notification.action.split('/bonuses/')[1];
		const isBonusAction = bonusId !== undefined;

		if (isBonusAction) {
			const bonusRoute = pathWithId(Route.Bonuses, bonusId);
			if (isRecognize) {
				window.location.href = monarchURL(bonusRoute);
			} else {
				navigate(bonusRoute);
			}

			handleClose();

			return;
		}

		const actionTypes = {
			spotlight_ready: () => {
				if (!notification.action) return;

				// Spotlight notifications are best opened in a new tab so users can keep context.
				// iOS (especially in-app webviews) can behave poorly with new-tab navigation.
				if (!isIOSDevice()) {
					const target = isRecognize ? monarchURL(notification.action) : notification.action;
					window.open(target, '_blank', 'noopener,noreferrer');
					return;
				}

				isRecognize ? (window.location.href = monarchURL(notification.action)) : navigate(notification.action);
			},
			feedback: () => {
				if (!notification.action) return;
				track(AnalyticsEvent.TopNavbar.ClickFeedbackNotification);
				const modifiedAction = notification.action.replace(/^(.*)\/app/, '');
				isRecognize ? (window.location.href = notification.action) : navigate(modifiedAction);
			},
			billing: () => {
				if (!notification.action) return;
				window.location.href = recognizeURL(notification.action);
				// TODO Investigate why billing does not navigate as other notifications
			},
			announcement: () => {
				if (!notification.action) return;
				const announcementId = notification.action.replace(/^announcement\//, '');
				dispatch(AnnouncementActionEvent.OpenAnnouncement, { id: announcementId });
			},
			default: () => {
				if (!notification.action) return;
				isRecognize ? (window.location.href = monarchURL(notification.action)) : navigate(notification.action);
			},
		};

		const matchType = Object.keys(actionTypes).find((type) => new RegExp(type).test(notification.type)) as keyof typeof actionTypes;
		const actionHandler = actionTypes[matchType] || actionTypes.default;
		actionHandler();

		handleClose();
	};

	return (
		<DropdownOption
			key={notification.id}
			w="100%"
			onClick={handleClick}
			onKeyDown={(e) => {
				if ([' ', 'Enter'].includes(e.key)) {
					handleClick();
					e.preventDefault();
				}
			}}
		>
			<Flex
				alignItems="flex-start"
				p={rem(8)}
				w="100%"
				justifyContent="space-between"
				gap={rem(12)}
				ref={ref}
				id={`notification-${notification.id}`}
				data-testid={`notification-${notification.id}`}
			>
				<Box w={rem(48)} justifyContent="center">
					<UnreadBadge isUnread={notification.status !== NotificationStatus.Read}>{icon}</UnreadBadge>
				</Box>
				<VStack w="100%" alignItems="flex-start" gap={0}>
					<Text fontSize="md" px={0} pb={rem(4)} pt={0}>
						{notification.message}
					</Text>
					<Text fontSize="xs" textOverflow="ellipsis" whiteSpace="nowrap" p={0} textColor="gray.700">
						{notification.time_ago}
					</Text>
				</VStack>
			</Flex>
		</DropdownOption>
	);
};
